// src/components/EarlyAccessModal.jsx
import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

export default function EarlyAccessModal({
  open,
  onContinue,
  onCancel,
  dontAskAgain,
  setDontAskAgain,
}) {
  const { i18n } = useTranslation();

  const overlayRef = useRef(null);
  const dialogRef = useRef(null);
  const prevFocusRef = useRef(null);

  const isEn = String(i18n?.language || '').toLowerCase().startsWith('en');

  const copy = isEn
    ? {
        title: 'Early access: AI Mentor / AI Assistant (Beta)',
        p1:
          'Our AI mentor / AI assistant is still in an early stage of development. You can already test it today, but answers may occasionally be incomplete or inaccurate.',
        p2: 'We appreciate your feedback – it helps us improve quality quickly.',
        dontShow: "Don't show this message again",
        cancel: 'Cancel',
        continue: 'Continue',
      }
    : {
        title: 'Früher Zugriff: KI-Mentor / KI-Assistent (Beta)',
        p1:
          'Unser KI-Mentor bzw. KI-Assistent befindet sich noch in einem frühen Entwicklungsstadium. Er kann heute bereits getestet werden, Antworten können aber gelegentlich unvollständig oder ungenau sein.',
        p2: 'Wir freuen uns über dein Feedback – es hilft uns, die Qualität schnell zu verbessern.',
        dontShow: 'Diesen Hinweis nicht mehr anzeigen',
        cancel: 'Abbrechen',
        continue: 'Weiter',
      };

  // Fokus-Management + ESC/Tab-Fokusfalle
  useEffect(() => {
    if (!open) return;

    // Fokus merken und auf das Dialog-Element setzen
    prevFocusRef.current = document.activeElement;
    dialogRef.current?.focus?.();

    function onKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel?.();
        return;
      }
      if (e.key === 'Tab') {
        // Fokusfalle (einfach gehalten)
        const focusable = dialogRef.current?.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (!focusable || focusable.length === 0) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];

        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }
    }

    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      // Fokus zurück
      prevFocusRef.current?.focus?.();
    };
  }, [open, onCancel]);

  // Body scroll lock während offen
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  // Klick auf Overlay schließt
  function onOverlayMouseDown(e) {
    if (e.target === overlayRef.current) {
      onCancel?.();
    }
  }

  // Nicht rendern, wenn geschlossen
  if (!open) return null;

  const overlayStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.55)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '1rem',
  };

  const dialogStyle = {
    width: '100%',
    maxWidth: 520,
    background: '#111827',
    color: '#e5e7eb',
    border: '1px solid #1f2937',
    borderRadius: 16,
    boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
    padding: '1.25rem',
    outline: 'none',
  };

  const titleStyle = { fontSize: '1.15rem', fontWeight: 800, marginBottom: '0.5rem' };
  const pStyle = { lineHeight: 1.55, color: '#cbd5e1', margin: '0.5rem 0' };

  const actionsStyle = {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.5rem',
    marginTop: '1rem',
  };

  const btnPrimary = {
    background: '#9effa8',
    color: '#0b0f0a',
    border: 'none',
    borderRadius: 10,
    padding: '0.55rem 0.9rem',
    fontWeight: 800,
    cursor: 'pointer',
  };

  const btnGhost = {
    background: '#1f2937',
    color: '#e5e7eb',
    border: '1px solid #334155',
    borderRadius: 10,
    padding: '0.55rem 0.9rem',
    fontWeight: 700,
    cursor: 'pointer',
  };

  const checkWrap = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginTop: '0.5rem',
    color: '#cbd5e1',
    fontSize: '0.95rem',
  };

  return (
    <div
      ref={overlayRef}
      style={overlayStyle}
      onMouseDown={onOverlayMouseDown}
      aria-modal="true"
      role="dialog"
      aria-labelledby="ai-early-title"
    >
      <div ref={dialogRef} style={dialogStyle} tabIndex={-1}>
        <h3 id="ai-early-title" style={titleStyle}>
          {copy.title}
        </h3>

        <p style={pStyle}>{copy.p1}</p>
        <p style={pStyle}>{copy.p2}</p>

        <label style={checkWrap}>
          <input
            type="checkbox"
            checked={!!dontAskAgain}
            onChange={(e) => setDontAskAgain?.(e.target.checked)}
          />
          {copy.dontShow}
        </label>

        <div style={actionsStyle}>
          <button type="button" style={btnGhost} onClick={onCancel}>
            {copy.cancel}
          </button>
          <button type="button" style={btnPrimary} onClick={onContinue}>
            {copy.continue}
          </button>
        </div>
      </div>
    </div>
  );
}
